import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { scheduledEmailsApi } from "../../../services/api/scheduledEmails";
import Icon from "../../../components/AppIcon";
import PageLayout from "../../../components/navigation/PageLayout";
import Pagination from "../../../components/ui/Pagination";
import ResultsHeader from "../../../components/ui/ResultsHeader";
import FilterPanel, {
  FilterRow,
  FilterField,
} from "../../../components/ui/FilterPanel";

const DEFAULT_PAGE_SIZE = 25;

const STATUS_BADGES = {
  Pending: { class: "badge badge-warning", icon: "Clock" },
  Processing: { class: "badge badge-info", icon: "Loader" },
  Sent: { class: "badge badge-success", icon: "CheckCircle" },
  Failed: { class: "badge badge-error", icon: "XCircle" },
  Cancelled: { class: "badge badge-neutral", icon: "Ban" },
};

const EMAIL_TYPE_LABELS = {
  UserInvitation: "Invitation",
  PasswordReset: "Password Reset",
  EmailVerification: "Verification",
  DistributionNotification: "Distribution",
  Welcome: "Welcome",
  AccountLocked: "Account Locked",
  SystemNotification: "System",
};

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
};

const StatusBadge = ({ status }) => {
  const badge = STATUS_BADGES[status] || { class: "badge", icon: "Circle" };
  return (
    <span className={badge.class}>
      <Icon name={badge.icon} size={12} className="mr-1" />
      {status}
    </span>
  );
};

const ScheduledEmailsPage = () => {
  const { isGlobalAdmin } = useAuth();
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Send test email
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testName, setTestName] = useState("");
  const [testSending, setTestSending] = useState(false);

  // Process now
  const [processLoading, setProcessLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: "",
    emailType: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 6000);
  };

  const fetchEmails = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      setLoading(true);
      setError(null);
      try {
        const response = await scheduledEmailsApi.getEmails({
          status: filters.status || null,
          emailType: filters.emailType || null,
          search: filters.search || null,
          page,
          pageSize,
        });

        if (response.success !== false) {
          setEmails(response.data || []);
          setPagination((prev) => ({
            ...prev,
            page: response.page || page,
            pageSize: response.pageSize || pageSize,
            totalCount: response.totalCount || 0,
            totalPages: response.totalPages || 0,
          }));
        }
      } catch (err) {
        setError(err.message || "Failed to load emails");
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  const fetchStats = useCallback(async () => {
    try {
      const response = await scheduledEmailsApi.getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Failed to load email stats:", err);
    }
  }, []);

  useEffect(() => {
    fetchEmails(1, pagination.pageSize);
    fetchStats();
  }, [filters]);

  const handleViewDetail = async (email) => {
    setSelectedEmail(email);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const response = await scheduledEmailsApi.getEmail(email.id);
      if (response.success && response.data) {
        setDetailData(response.data);
      }
    } catch (err) {
      console.error("Failed to load email detail:", err);
      setDetailData(email);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      const response = await scheduledEmailsApi.cancel(id);
      if (response.success) {
        showNotification("success", "Email cancelled.");
        fetchEmails(pagination.page, pagination.pageSize);
        fetchStats();
      } else {
        showNotification(
          "error",
          response.message || "Failed to cancel email.",
        );
      }
    } catch (err) {
      showNotification("error", err.message || "Failed to cancel email.");
    }
  };

  const handleRetry = async (id) => {
    try {
      const response = await scheduledEmailsApi.retry(id);
      if (response.success) {
        showNotification("success", "Email queued for retry.");
        fetchEmails(pagination.page, pagination.pageSize);
        fetchStats();
      } else {
        showNotification("error", response.message || "Failed to retry email.");
      }
    } catch (err) {
      showNotification("error", err.message || "Failed to retry email.");
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setTestSending(true);
    try {
      const response = await scheduledEmailsApi.sendTest({
        toEmail: testEmail,
        toName: testName || null,
      });
      if (response.success && response.data?.success) {
        showNotification(
          "success",
          `Test email sent to ${testEmail}! MessageId: ${response.data.messageId || "N/A"}`,
        );
        setShowTestModal(false);
        setTestEmail("");
        setTestName("");
      } else {
        showNotification(
          "error",
          response.data?.error ||
            response.message ||
            "Failed to send test email.",
        );
      }
    } catch (err) {
      showNotification("error", err.message || "Failed to send test email.");
    } finally {
      setTestSending(false);
    }
  };

  const handleProcessNow = async () => {
    if (
      !window.confirm(
        "Process all pending emails now? This will send them immediately.",
      )
    )
      return;
    setProcessLoading(true);
    try {
      const response = await scheduledEmailsApi.processNow();
      if (response.success && response.data) {
        const d = response.data;
        showNotification(
          d.failed > 0 ? "warning" : "success",
          `Processed ${d.processed} emails: ${d.succeeded} sent, ${d.failed} failed.`,
        );
        fetchEmails(pagination.page, pagination.pageSize);
        fetchStats();
      } else {
        showNotification(
          "info",
          response.message || "No pending emails to process.",
        );
      }
    } catch (err) {
      showNotification("error", err.message || "Failed to process emails.");
    } finally {
      setProcessLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    fetchEmails(newPage, pagination.pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    fetchEmails(1, newPageSize);
  };

  const handleResetFilters = () => {
    setFilters({ status: "", emailType: "", search: "" });
  };

  const hasActiveFilters =
    filters.status || filters.emailType || filters.search;
  const activeFilterCount = [
    filters.status,
    filters.emailType,
    filters.search,
  ].filter(Boolean).length;

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "System", path: "/system" },
    { label: "Scheduled Emails", path: "/system/scheduled-emails" },
  ];

  if (!isGlobalAdmin) {
    return (
      <PageLayout
        title="Access Denied"
        description="You don't have permission to view this page."
        breadcrumbs={breadcrumbs}
      >
        <div className="card p-8 text-center">
          <Icon
            name="ShieldAlert"
            size={48}
            className="mx-auto mb-4 text-warning"
          />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            Only Global Administrators can access scheduled emails.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Scheduled Emails - Rocket Tronc</title>
      </Helmet>

      <PageLayout
        title="Scheduled Emails"
        description="View email queue, sent emails, send test emails, and manage delivery."
        breadcrumbs={breadcrumbs}
      >
        {/* Notification */}
        {notification && (
          <div
            className={`alert ${
              notification.type === "success"
                ? "alert-success"
                : notification.type === "warning"
                  ? "alert-warning"
                  : notification.type === "info"
                    ? "alert-info"
                    : "alert-error"
            } mb-6`}
          >
            <Icon
              name={
                notification.type === "success"
                  ? "CheckCircle"
                  : notification.type === "warning"
                    ? "AlertTriangle"
                    : notification.type === "info"
                      ? "Info"
                      : "AlertCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setNotification(null)}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setError(null)}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p
                    className={`text-2xl font-bold font-data ${stats.pending > 0 ? "text-warning" : ""}`}
                  >
                    {stats.pending}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sent (24h)</p>
                  <p className="text-2xl font-bold font-data text-success">
                    {stats.sentLast24Hours}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Sent</p>
                  <p className="text-2xl font-bold font-data">{stats.sent}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Send" size={20} className="text-primary" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Failed (7d)</p>
                  <p
                    className={`text-2xl font-bold font-data ${stats.failedLast7Days > 0 ? "text-error" : ""}`}
                  >
                    {stats.failedLast7Days}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
                  <Icon name="XCircle" size={20} className="text-error" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            className="btn btn-primary"
            onClick={() => setShowTestModal(true)}
          >
            <Icon name="FlaskConical" size={18} className="mr-2" />
            Send Test Email
          </button>
          <button
            className="btn btn-outline"
            onClick={handleProcessNow}
            disabled={processLoading || !stats?.pending}
          >
            {processLoading ? (
              <div className="spinner w-4 h-4 mr-2" />
            ) : (
              <Icon name="Zap" size={18} className="mr-2" />
            )}
            Process All Pending ({stats?.pending || 0})
          </button>
        </div>

        {/* Filters */}
        <FilterPanel
          title="Filters"
          onReset={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          activeFilterCount={activeFilterCount}
          className="mb-6"
        >
          <FilterRow>
            <FilterField label="Status">
              <select
                className="input w-full"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Sent">Sent</option>
                <option value="Failed">Failed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </FilterField>

            <FilterField label="Email Type">
              <select
                className="input w-full"
                value={filters.emailType}
                onChange={(e) =>
                  handleFilterChange("emailType", e.target.value)
                }
              >
                <option value="">All Types</option>
                <option value="UserInvitation">Invitation</option>
                <option value="PasswordReset">Password Reset</option>
                <option value="Welcome">Welcome</option>
                <option value="DistributionNotification">Distribution</option>
                <option value="SystemNotification">System</option>
                <option value="AccountLocked">Account Locked</option>
              </select>
            </FilterField>

            <FilterField label="Search">
              <input
                className="input w-full"
                type="text"
                placeholder="Email address or subject..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </FilterField>
          </FilterRow>
        </FilterPanel>

        {/* Results Header */}
        <ResultsHeader totalCount={pagination.totalCount} entityName="emails">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => fetchEmails(pagination.page, pagination.pageSize)}
          >
            <Icon name="RefreshCw" size={16} />
            Refresh
          </button>
        </ResultsHeader>

        {/* Emails Table */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Sent</th>
                  <th>Attempts</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : emails.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Icon
                          name="Mail"
                          size={32}
                          className="text-muted-foreground"
                        />
                        <span>No emails found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  emails.map((email) => (
                    <tr key={email.id} className="hover:bg-muted/50">
                      <td className="font-data text-xs whitespace-nowrap">
                        {formatTimestamp(email.createdOn)}
                      </td>
                      <td>
                        <StatusBadge status={email.status} />
                      </td>
                      <td className="text-xs">
                        <span className="badge badge-neutral">
                          {EMAIL_TYPE_LABELS[email.emailType] ||
                            email.emailType}
                        </span>
                      </td>
                      <td className="text-sm">
                        <div
                          className="truncate max-w-[180px]"
                          title={email.toEmail}
                        >
                          {email.toEmail}
                        </div>
                        {email.toName && (
                          <div className="text-xs text-muted-foreground truncate">
                            {email.toName}
                          </div>
                        )}
                      </td>
                      <td
                        className="text-sm max-w-[200px] truncate"
                        title={email.subject}
                      >
                        {email.subject}
                      </td>
                      <td className="font-data text-xs whitespace-nowrap">
                        {email.sentOn ? formatTimestamp(email.sentOn) : "-"}
                      </td>
                      <td className="text-xs text-center">
                        <span
                          className={
                            email.attemptCount >= email.maxAttempts &&
                            email.status === "Failed"
                              ? "text-error font-semibold"
                              : ""
                          }
                        >
                          {email.attemptCount}/{email.maxAttempts}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleViewDetail(email)}
                            title="View details"
                          >
                            <Icon name="Eye" size={16} />
                          </button>
                          {email.status === "Pending" && (
                            <button
                              className="btn btn-ghost btn-sm text-warning"
                              onClick={() => handleCancel(email.id)}
                              title="Cancel"
                            >
                              <Icon name="Ban" size={16} />
                            </button>
                          )}
                          {email.status === "Failed" && (
                            <button
                              className="btn btn-ghost btn-sm text-primary"
                              onClick={() => handleRetry(email.id)}
                              title="Retry"
                            >
                              <Icon name="RotateCw" size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={pagination.totalCount}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>

        {/* Detail Modal */}
        {selectedEmail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Mail" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Email Details</h2>
                    <p className="text-xs text-muted-foreground font-mono">
                      {selectedEmail.id}
                    </p>
                  </div>
                </div>
                <button
                  className="p-2 hover:bg-muted rounded-lg"
                  onClick={() => {
                    setSelectedEmail(null);
                    setDetailData(null);
                  }}
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-4">
                {detailLoading ? (
                  <div className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </div>
                ) : (
                  <>
                    {/* Summary Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        <StatusBadge
                          status={detailData?.status || selectedEmail.status}
                        />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Type</p>
                        <span className="badge badge-neutral">
                          {EMAIL_TYPE_LABELS[
                            detailData?.emailType || selectedEmail.emailType
                          ] ||
                            detailData?.emailType ||
                            selectedEmail.emailType}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Priority
                        </p>
                        <p className="text-sm">
                          {detailData?.priority || selectedEmail.priority}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Recipient
                        </p>
                        <p className="text-sm font-medium">
                          {detailData?.toEmail || selectedEmail.toEmail}
                        </p>
                        {(detailData?.toName || selectedEmail.toName) && (
                          <p className="text-xs text-muted-foreground">
                            {detailData?.toName || selectedEmail.toName}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="font-data text-sm">
                          {formatTimestamp(
                            detailData?.createdOn || selectedEmail.createdOn,
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Sent</p>
                        <p className="font-data text-sm">
                          {formatTimestamp(
                            detailData?.sentOn || selectedEmail.sentOn,
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Attempts
                        </p>
                        <p className="text-sm">
                          {detailData?.attemptCount ||
                            selectedEmail.attemptCount}
                          /
                          {detailData?.maxAttempts || selectedEmail.maxAttempts}
                        </p>
                      </div>
                      {(detailData?.externalMessageId ||
                        selectedEmail.externalMessageId) && (
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground">
                            SES Message ID
                          </p>
                          <p className="font-mono text-xs break-all">
                            {detailData?.externalMessageId ||
                              selectedEmail.externalMessageId}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Subject */}
                    <div className="border-t border-border pt-4">
                      <p className="text-xs text-muted-foreground mb-1">
                        Subject
                      </p>
                      <p className="font-semibold">
                        {detailData?.subject || selectedEmail.subject}
                      </p>
                    </div>

                    {/* Error */}
                    {(detailData?.lastError || selectedEmail.lastError) && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Last Error
                        </p>
                        <pre className="bg-error/5 border border-error/20 text-error p-3 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap break-words">
                          {detailData?.lastError || selectedEmail.lastError}
                        </pre>
                      </div>
                    )}

                    {/* HTML Preview */}
                    {detailData?.htmlBody && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Email Preview
                        </p>
                        <div className="border border-border rounded-lg overflow-hidden bg-white">
                          <iframe
                            srcDoc={detailData.htmlBody}
                            title="Email preview"
                            className="w-full border-0"
                            style={{ minHeight: "400px" }}
                            sandbox="allow-same-origin"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Send Test Email Modal */}
        {showTestModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon
                        name="FlaskConical"
                        size={20}
                        className="text-primary"
                      />
                    </div>
                    <h2 className="text-lg font-semibold">Send Test Email</h2>
                  </div>
                  <button
                    className="p-2 hover:bg-muted rounded-lg"
                    onClick={() => setShowTestModal(false)}
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSendTest} className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Send a test email to verify your AWS SES configuration is
                  working.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Recipient Email *
                  </label>
                  <input
                    type="email"
                    className="input w-full"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Recipient Name (optional)
                  </label>
                  <input
                    type="text"
                    className="input w-full"
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    placeholder="John Doe"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setShowTestModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={testSending || !testEmail}
                  >
                    {testSending ? (
                      <>
                        <div className="spinner w-4 h-4 mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={18} className="mr-2" />
                        Send Test
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default ScheduledEmailsPage;
